import React, { useContext, useMemo, useState } from "react";
import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Select,
    Text,
    IconButton,
    Box,
    HStack,
} from "@chakra-ui/react";
import { FaImage, FaFileAlt, FaCheck } from "react-icons/fa";
import TicketDetailsModal from "../TicketDetailsModal";
import { useNavigate } from "react-router-dom";
import { recurringTicketStatuses, ticketRecurrences } from "../../../constants/ticketStatusConstants";
import { stickyColHeaderProps, stickyColCellProps } from "../../../constants/tableStyles";
import { parseApiDate } from "../../../utils/date";
import { AuthContext } from "../../../context/AuthContext";

const RecurringTicketTable = ({ recurringTickets, confirmCompletionMark, confirmRecurrenceChange, confirmStatusChange, recurrence, status, handleImagePreview }) => {
    const { isAdmin } = useContext(AuthContext);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [sortOrder, setSortOrder] = useState("asc");
    const [sortBy, setSortBy] = useState("sectionName");
    const navigate = useNavigate();
    const [selectedTicket, setSelectedTicket] = useState(null);

    const handleDetailsClick = (ticket) => {
        setSelectedTicket(ticket);
        setIsModalOpen(true);
    };

    const handleFullDetails = (id) => {
        navigate(`/recurring-details/${id}`);
      };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedTicket(null);
    };

    const truncateText = (text, maxLength = 30) => {
        return typeof text === 'undefined' ? text : (text.length > maxLength ? text.substring(0, maxLength) + "..." : text);

    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortOrder("asc");
        }
    };

    const sortedTickets = useMemo(() => {
        return [...recurringTickets]
            .filter((ticket) => ticket.status === status && ticket.recurrence === recurrence)
            .sort((a, b) => {
                if (sortOrder === "asc") {
                    return a[sortBy] > b[sortBy] ? 1 : -1;
                } else {
                    return a[sortBy] < b[sortBy] ? 1 : -1;
                }
            });
    }, [recurringTickets, status, recurrence, sortBy, sortOrder]);

    return (
        <>
            {sortedTickets.length > 0 ? (
                <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.100" boxShadow="card" p={3}>
                <TableContainer overflowX="auto">
                    <Table variant="striped" colorScheme="gray" size="sm">
                        <Thead>
                            <Tr>
                                <Th {...stickyColHeaderProps}>#</Th>
                                <Th onClick={() => handleSort("name")}>
                                    Naziv {sortBy === "name" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                                </Th>
                                <Th cursor="pointer" onClick={() => handleSort("lastCompletedAt")}>
                                    Zadnje odrađeno {sortBy === "lastCompletedAt" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                                </Th>
                                <Th onClick={() => handleSort("lastCompletedByName")}>
                                    Zadnji odradio {sortBy === "lastCompletedByName" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                                </Th>
                                
                                {status === 10 ?
                                    <Th>
                                        Označi 
                                    </Th> 
                                : null }
                                <Th>Akcije</Th>
                                {isAdmin && <Th>Status</Th>}
                                <Th>Tip</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {sortedTickets.map((ticket, index) => (
                                <Tr key={ticket.id} _hover={{ bg: "gray.50" }}>
                                    <Td {...stickyColCellProps(index % 2 === 0)}>{index + 1}</Td>

                                    <Td onDoubleClick={() => handleFullDetails(ticket.id)} fontWeight="bold">
                                        {truncateText(ticket.name)}
                                    </Td>
                                    
                                    <Td>
                                        {ticket.lastCompletedByName !== '' ? 
                                        <>
                                            {parseApiDate(ticket.lastCompletedAt).toLocaleDateString('de-DE', { timeZone: 'Europe/Zagreb' }).replace(/\//g, '.')} |&nbsp;
                                            {parseApiDate(ticket.lastCompletedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Zagreb' })}
                                        </> 
                                        : " - "}
                                    </Td>
                                    <Td onDoubleClick={() => handleFullDetails(ticket.id)}>
                                        {ticket.lastCompletedByName !== '' ? truncateText(ticket.lastCompletedByName) : " - "}
                                    </Td>
                                    {ticket.status === 10 ?
                                    <Td>
                                        <HStack spacing={2}> 
                                            <Box as="span" border="1px solid #154360" borderRadius="md" p={1}>
                                                <IconButton
                                                    aria-label="Mark as complete"
                                                    icon={<FaCheck />}
                                                    color="brand.700"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => confirmCompletionMark(ticket)}
                                                />
                                            </Box>
                                        </HStack>
                                    </Td>
                                    : null }
                                    <Td>
                                        <HStack spacing={2}>
                                            {ticket.imageUrl && (
                                                <Box as="span" border="1px solid #154360" borderRadius="md" p={1}>
                                                    <IconButton
                                                        aria-label="Preview Image"
                                                        icon={<FaImage />}
                                                        color="brand.700"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleImagePreview(ticket.imageUrl)}
                                                    />
                                                </Box>
                                            )}
                                            <Box as="span" border="1px solid #154360" borderRadius="md" p={1}>
                                                <IconButton
                                                    aria-label="View Details"
                                                    icon={<FaFileAlt />}
                                                    color="brand.700"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDetailsClick(ticket)}
                                                />
                                            </Box>
                                        </HStack>
                                    </Td>
                                    
                                    {isAdmin && (
                                        <Td>
                                            <Select
                                                size="sm"
                                                minW="150px"
                                                width="100%"
                                                value={ticket.status}
                                                onChange={(e) => confirmStatusChange(ticket, parseInt(e.target.value))}
                                            >
                                                {recurringTicketStatuses.map((status) => (
                                                    <option key={status.code} value={status.code}>
                                                        {status.name}
                                                    </option>
                                                ))}
                                            </Select>
                                        </Td>
                                    )}
                                    <Td>
                                        <Select
                                            size="sm"
                                            minW="150px"
                                            width="100%"
                                            value={ticket.recurrence}
                                            onChange={(e) => confirmRecurrenceChange(ticket, parseInt(e.target.value))}
                                        >
                                            {ticketRecurrences.map((recurrence) => (
                                                <option key={recurrence.code} value={recurrence.code}>
                                                    {recurrence.name}
                                                </option>
                                            ))}
                                        </Select>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>
                </Box>
            ) : (
                <Text>Zadaci se učitavaju ili ne postoje u navedenom statusu.</Text>
            )}
            {isModalOpen && selectedTicket && <TicketDetailsModal ticket={selectedTicket} onClose={closeModal} />}
        </>
    );
};

export default React.memo(RecurringTicketTable);